import {
    ActionIcon,
    Alert,
    Button,
    Divider,
    Group,
    Loader,
    NumberInput,
    Paper,
    Stack,
    Text,
    Title,
} from '@mantine/core'
import {notifications} from '@mantine/notifications'
import {IconMinus, IconPlus} from '@tabler/icons-react'
import {Link} from '@tanstack/react-router'
import {useState} from 'react'

import {isAdmin} from 'src/auth/is-admin'
import {useCurrentUser} from 'src/auth/use-current-user'
import {DetailPageBackLink} from 'src/ui/detail-page-back-link'

import {
    formatStockAdjustmentSuccessMessage,
    formatStockAdjustmentWarningMessage,
} from './format-stock-adjustment-message'
import type {StockLevelAdjustmentResponse} from './schemas'
import {StockCommitmentChart} from './stock-commitment-chart'
import {StockJobCommitmentsTable} from './stock-job-commitments-table'
import {useAdjustStockLevel, useStockSummary} from './use-equipment-stock'

type StockOverviewPageProps = {
    typeId: string
}

export function StockOverviewPage({typeId}: StockOverviewPageProps) {
    const user = useCurrentUser()
    const {data: summary, isLoading, isError, error, refetch} = useStockSummary(typeId)
    const adjustStockLevel = useAdjustStockLevel(typeId)
    const [delta, setDelta] = useState(1)
    const [lastAdjustment, setLastAdjustment] = useState<StockLevelAdjustmentResponse | null>(null)

    if (isLoading) {
        return (
            <Group justify="center" py="xl">
                <Loader />
            </Group>
        )
    }

    if (isError || !summary) {
        const message = error instanceof Error ? error.message : 'Failed to load stock summary'
        const isStockTrackedError = message.toLowerCase().includes('stock-tracked')

        return (
            <Stack gap="md">
                <Title order={1}>Overview</Title>
                <Alert
                    color="red"
                    title={
                        isStockTrackedError
                            ? 'Not a stock-tracked type'
                            : 'Could not load stock summary'
                    }
                >
                    {message}
                </Alert>
                {!isStockTrackedError && (
                    <Button onClick={() => void refetch()} variant="light">
                        Retry
                    </Button>
                )}
            </Stack>
        )
    }

    const adjustmentWarning = lastAdjustment
        ? formatStockAdjustmentWarningMessage(lastAdjustment)
        : null

    return (
        <Stack gap="md">
            <Stack gap={4}>
                <DetailPageBackLink to="/rental-assets" listLabel="equipment" />
                <Title order={1}>{summary.typeName} — Overview</Title>
            </Stack>

            <Paper withBorder p="md" radius="md">
                <Stack gap="sm">
                    <Group align="flex-start" wrap="wrap" gap="xl">
                        <div>
                            <Text size="sm" c="dimmed">
                                Stock level
                            </Text>
                            <Text fw={600} data-testid="stock-level-count">
                                {summary.totalCount}
                            </Text>
                        </div>
                        <div>
                            <Text size="sm" c="dimmed">
                                Adjust stock level
                            </Text>
                            {isAdmin(user) ? (
                                <Group align="center" wrap="nowrap" gap="xs" mt={4}>
                                    <ActionIcon
                                        aria-label="Decrease adjustment"
                                        variant="default"
                                        size="lg"
                                        onClick={() => setDelta((current) => current - 1)}
                                    >
                                        <IconMinus size={16} />
                                    </ActionIcon>
                                    <NumberInput
                                        aria-label="Stock adjustment delta"
                                        data-testid="stock-adjustment-delta"
                                        value={delta}
                                        onChange={(value) => {
                                            if (
                                                typeof value === 'number' &&
                                                Number.isFinite(value)
                                            ) {
                                                setDelta(value)
                                            }
                                        }}
                                        allowDecimal={false}
                                        style={{width: 96}}
                                    />
                                    <ActionIcon
                                        aria-label="Increase adjustment"
                                        variant="default"
                                        size="lg"
                                        onClick={() => setDelta((current) => current + 1)}
                                    >
                                        <IconPlus size={16} />
                                    </ActionIcon>
                                    <Button
                                        loading={adjustStockLevel.isPending}
                                        onClick={() => {
                                            void adjustStockLevel
                                                .mutateAsync({delta})
                                                .then((result) => {
                                                    setLastAdjustment(result)
                                                    notifications.show({
                                                        color:
                                                            result.blockedCount > 0
                                                                ? 'yellow'
                                                                : 'green',
                                                        message:
                                                            formatStockAdjustmentSuccessMessage(
                                                                result,
                                                            ),
                                                    })
                                                    const warning =
                                                        formatStockAdjustmentWarningMessage(result)
                                                    if (warning) {
                                                        notifications.show({
                                                            color: 'yellow',
                                                            title: 'Some units could not be removed',
                                                            message: warning,
                                                            autoClose: false,
                                                        })
                                                    }
                                                })
                                        }}
                                    >
                                        Apply
                                    </Button>
                                </Group>
                            ) : (
                                <Text mt={4} c="dimmed">
                                    Only admins can adjust stock levels.
                                </Text>
                            )}
                        </div>
                    </Group>
                    {adjustmentWarning && (
                        <Alert color="yellow" title="Some units could not be removed">
                            {adjustmentWarning}
                        </Alert>
                    )}
                </Stack>
            </Paper>

            <Paper withBorder p="md" radius="md">
                <Stack gap="md">
                    <Stack gap={4}>
                        <Title order={4}>Availability</Title>
                        <Text size="sm" c="dimmed">
                            A committed unit stays unavailable until it is physically returned, not
                            on a date. Units in the field have no known return.
                        </Text>
                    </Stack>
                    <StockCommitmentChart
                        days={summary.commitmentTimeline}
                        totalCount={summary.totalCount}
                    />
                    <Divider />
                    <Stack gap={4}>
                        <Title order={5}>Where these units are</Title>
                        <Text size="sm" c="dimmed">
                            Assigned = on site + awaiting deployment + returned, matching the order
                            equipment view. Returned units are back in the pool and count as free
                            above.
                        </Text>
                    </Stack>
                    <StockJobCommitmentsTable commitments={summary.jobCommitments} />
                </Stack>
            </Paper>

            <Text size="sm" c="dimmed">
                <Link to="/rental-assets/types/$typeId" params={{typeId}}>
                    View type settings
                </Link>
            </Text>
        </Stack>
    )
}
